import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * Free shipping progress announcement.
 *
 * Renders one of three states from the live cart total:
 *   empty    → advertises the threshold ("Free shipping over $100")
 *   progress → counts down the amount remaining to qualify
 *   success  → threshold met ("You've got free shipping")
 *
 * Server-side Liquid paints the correct initial text on page load; this
 * element keeps it in sync on every add / quantity change / remove by
 * listening for Shopify's cartLinesUpdate event (the same event the cart
 * icon uses).
 *
 * The template token is [amount]. Threshold math is done in minor units
 * (cents); data-threshold-cents is computed in Liquid as (threshold * 100),
 * which assumes a 2-decimal currency such as USD.
 */
class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.msgEl = this.querySelector('.free-shipping-bar__msg');
    this.showMeter = this.dataset.showMeter === 'true';
    this.threshold = parseInt(this.dataset.thresholdCents ?? '0', 10);
    this.currency = this.dataset.currency || 'USD';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.templates = {
      empty: this.dataset.msgEmpty || '',
      progress: this.dataset.msgProgress || '',
      success: this.dataset.msgSuccess || '',
    };
    this.meterCount = parseInt(this.dataset.meterCount ?? '10', 10);
    this.starFilled = this.dataset.starFilled || '✦';
    this.starEmpty = this.dataset.starEmpty || '✧';

    this.onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  /**
   * @param {{ promise?: Promise<{ cart: object | null }> }} event
   */
  onCartUpdate(event) {
    Promise.resolve(event?.promise)
      .then((result) => {
        const cart = result?.cart;
        const amount = cart?.cost?.totalAmount;
        if (amount) {
          const cents = convertMoneyToMinorUnits(amount.amount, amount.currencyCode || this.currency);
          this.render(cents ?? 0, cart.totalQuantity ?? 0);
        } else {
          // Event fired without a resolved cart — read it directly.
          return this.refetch();
        }
      })
      .catch(() => this.refetch());
  }

  async refetch() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.render(cart.total_price ?? 0, cart.item_count ?? 0);
    } catch (e) {
      // Leave the server-rendered message in place on failure.
    }
  }

  /**
   * @param {number} totalCents - current cart total in minor units
   * @param {number} itemCount  - number of items in the cart
   */
  render(totalCents, itemCount) {
    if (!this.msgEl) return;

    if (itemCount <= 0) {
      this.msgEl.textContent = this.strip(this.fill(this.templates.empty, this.threshold));
    } else if (totalCents >= this.threshold) {
      this.msgEl.textContent = this.strip(this.templates.success);
    } else {
      this.renderProgress(totalCents);
    }
  }

  /**
   * Renders the countdown state, inlining the star meter at the [stars] token.
   * @param {number} totalCents
   */
  renderProgress(totalCents) {
    const msg = this.fill(this.templates.progress, this.threshold - totalCents);
    const idx = this.showMeter ? msg.indexOf('[stars]') : -1;

    if (idx === -1) {
      // Meter off or no token — plain text, token stripped.
      this.msgEl.textContent = msg.replace(/\[stars\]/g, '');
      return;
    }

    const before = msg.slice(0, idx);
    const after = msg.slice(idx + '[stars]'.length);

    // Wrap the text fragments in spans (not bare text nodes) so the mobile
    // CSS can flex-reorder the meter onto its own line without the leading/
    // trailing space at the split collapsing away. Mirrors the Liquid markup.
    this.msgEl.textContent = '';
    this.msgEl.append(
      this.makeText(before),
      this.makeMeter(totalCents),
      this.makeText(after)
    );
  }

  /** @param {string} text */
  makeText(text) {
    const span = document.createElement('span');
    span.className = 'free-shipping-bar__text';
    span.textContent = text;
    return span;
  }

  /**
   * Builds the star meter element: filled stars proportional to cart progress.
   * @param {number} totalCents
   */
  makeMeter(totalCents) {
    const ratio = this.threshold > 0 ? totalCents / this.threshold : 0;
    let filled = Math.round(ratio * this.meterCount);
    filled = Math.max(0, Math.min(this.meterCount, filled));

    const meter = document.createElement('span');
    meter.className = 'free-shipping-bar__meter';
    meter.setAttribute('aria-hidden', 'true');
    // Filled stars sit in their own span so CSS can paint them with the
    // gold-foil texture (mirrors the Liquid markup).
    const filledSpan = document.createElement('span');
    filledSpan.className = 'free-shipping-bar__meter-filled';
    filledSpan.textContent = this.starFilled.repeat(filled);
    meter.append(filledSpan, document.createTextNode(this.starEmpty.repeat(this.meterCount - filled)));
    return meter;
  }

  /** @param {string} value */
  strip(value) {
    return value.trim();
  }

  /**
   * Substitutes the [amount] token with a formatted money value.
   * @param {string} template
   * @param {number} cents
   */
  fill(template, cents) {
    return template.replace(/\[amount\]/g, formatMoney(cents, this.moneyFormat, this.currency));
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
